'use client';

import { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Article {
    id: string;
    title: string;
    status: 'draft' | 'published' | 'scheduled';
    updatedAt?: number;
    contentUpdatedAt?: number;
    scheduledAt?: number;
}

interface PublishingCalendarProps {
    articles: Article[];
    onDateSelect?: (date: Date) => void;
}

export default function PublishingCalendar({ articles, onDateSelect }: PublishingCalendarProps) {
    const [currentDate, setCurrentDate] = useState(new Date());

    // Get first and last day of current month
    const firstDayOfMonth = useMemo(() => {
        return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    }, [currentDate]);

    const lastDayOfMonth = useMemo(() => {
        return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    }, [currentDate]);

    // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
    const startingDayOfWeek = firstDayOfMonth.getDay();

    // Group articles by date
    const articlesByDate = useMemo(() => {
        const grouped: Record<string, Article[]> = {};

        articles.forEach(article => {
            // Use scheduledAt if scheduled, otherwise use updated date
            const timestamp = article.scheduledAt || article.contentUpdatedAt || article.updatedAt;
            if (timestamp) {
                const date = new Date(timestamp * 1000);
                const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                if (!grouped[dateKey]) {
                    grouped[dateKey] = [];
                }
                grouped[dateKey].push(article);
            }
        });

        return grouped;
    }, [articles]);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const handleDateClick = (day: number) => {
        const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        onDateSelect?.(selectedDate);
    };

    // Generate calendar days
    const calendarDays = [];
    const daysInMonth = lastDayOfMonth.getDate();

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
        calendarDays.push(<div key={`empty-${i}`} className="h-20 bg-gray-50"></div>);
    }

    // Add days of the month
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() &&
        currentDate.getFullYear() === today.getFullYear();

    for (let day = 1; day <= daysInMonth; day++) {
        const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${day}`;
        const dayArticles = articlesByDate[dateKey] || [];
        const isToday = isCurrentMonth && day === today.getDate();

        const draftCount = dayArticles.filter(a => a.status === 'draft').length;
        const scheduledCount = dayArticles.filter(a => a.status === 'scheduled').length;
        const publishedCount = dayArticles.filter(a => a.status === 'published').length;

        calendarDays.push(
            <div
                key={day}
                onClick={() => handleDateClick(day)}
                className={`h-20 border border-gray-200 p-2 cursor-pointer transition-colors ${isToday
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white hover:bg-blue-50'
                    }`}
            >
                <div className={`text-sm font-medium ${isToday ? 'text-blue-700 font-semibold' : 'text-gray-900'}`}>
                    {day}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                    {draftCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full" title={`${draftCount} draft${draftCount > 1 ? 's' : ''}`}>
                            {draftCount}
                        </span>
                    )}
                    {scheduledCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-yellow-600 bg-yellow-100 rounded-full" title={`${scheduledCount} scheduled`}>
                            {scheduledCount}
                        </span>
                    )}
                    {publishedCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-green-600 bg-green-100 rounded-full" title={`${publishedCount} published`}>
                            ✓
                        </span>
                    )}
                </div>
            </div>
        );
    }

    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <span className="text-2xl mr-2">📅</span>
                    Publishing Calendar
                </h2>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleToday}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                    >
                        Today
                    </button>
                    <button
                        onClick={handlePrevMonth}
                        className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        title="Previous month"
                    >
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <span className="text-lg font-medium text-gray-900 min-w-[200px] text-center">
                        {monthName}
                    </span>
                    <button
                        onClick={handleNextMonth}
                        className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        title="Next month"
                    >
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="border-t border-gray-200">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-0 border-b border-gray-200">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="py-2 text-center text-sm font-medium text-gray-500 bg-gray-50">
                            {day}
                        </div>
                    ))}
                </div>
                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-0">
                    {calendarDays}
                </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                    <span className="inline-block w-4 h-4 bg-blue-100 rounded-full mr-2"></span>
                    Draft
                </div>
                <div className="flex items-center">
                    <span className="inline-block w-4 h-4 bg-yellow-100 rounded-full mr-2"></span>
                    Scheduled
                </div>
                <div className="flex items-center">
                    <span className="inline-block w-4 h-4 bg-green-100 rounded-full mr-2 flex items-center justify-center text-xs text-green-600">✓</span>
                    Published
                </div>
            </div>
        </div>
    );
}
